import moment from "moment";
import { CSSProperties, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";

import "../shared/league.css";
import { AnhPhongTo, Avatar, AvatarZoom, initials } from "../shared/Avatar";
import {
  Balls,
  BracketNode,
  CupVoDich,
  MatchRow,
  PlayerName,
  STATUS_META,
  VO_DICH,
  You,
} from "../shared/ui";
import { money } from "../shared/format";
import { fetchSwissView } from "./api";
import {
  SwissCurrentMatch,
  SwissMatch,
  SwissPlayoffRound,
  SwissRound,
  SwissStandingRow,
  SwissVenue,
  SwissView,
} from "./types";
import BilliardLoader from "../../../components/BilliardLoader";
import FeePaymentModal from "../../../components/league/FeePaymentModal";
import * as SBService from "../../../helpers/sportbookService";
import { pingChallengeDeclined } from "../../../helpers/swissChallengePing";
import { getAssetFullUrl } from "../../../helpers/utils";
import { swissRefreshState, userState } from "../../../states/common";

type Tab = "info" | "prize" | "rule" | "reg" | "play" | "rank";

const GroupChip = ({ losses, dead }: { losses: number; dead: boolean }) => {
  if (dead) {
    return (
      <span className="grpcell" style={{ color: "var(--st-dead)", border: "1px solid var(--line)" }}>
        Đã loại
      </span>
    );
  }
  const map: [string, string][] = [
    ["#B4CDBF", "rgba(110,139,124,.16)"],
    ["#E6D08F", "rgba(201,162,39,.14)"],
    ["#F0BDB4", "rgba(193,58,40,.16)"],
  ];
  const [color, background] = map[Math.min(losses, 2)];
  return (
    <span className="grpcell" style={{ color, background }}>
      {losses} thua
    </span>
  );
};

const RoundGroup = ({
  round,
  defaultOpen,
  filter,
  myPlayerId,
  openRounds,
  toggle,
}: {
  round: SwissRound;
  defaultOpen: boolean;
  filter: "all" | "mine";
  myPlayerId: string | null;
  openRounds: Record<string, boolean>;
  toggle: (key: string) => void;
}) => {
  const matches = filter === "mine" ? round.matches.filter((m) => m.mine) : round.matches;
  const key = "r" + round.round;
  const open = openRounds[key] !== undefined ? openRounds[key] : defaultOpen;
  if (filter === "mine" && matches.length === 0) {
    return null;
  }
  const done = matches.filter((m) => m.status === "CONFIRMED" || m.status === "BYE").length;

  return (
    <div className={`roundgrp ${open ? "" : "closed"}`}>
      <div className="rh" onClick={() => toggle(key)}>
        <span className="rt">Vòng {round.round}</span>
        {round.isOpen ? (
          <span className="chip live">
            <span className="pulse" />
            Đang diễn ra
          </span>
        ) : (
          <span className="chip done">Đã đóng</span>
        )}
        <span className="rc">
          {done}/{matches.length} trận {open ? "▾" : "▸"}
        </span>
      </div>
      <div className="rb">
        {matches.map((match) => (
          <MatchRow key={match.scheduleId} match={match} myPlayerId={myPlayerId} />
        ))}
      </div>
    </div>
  );
};

/**
 * Một vòng của nhánh loại trực tiếp.
 *
 * Khác RoundGroup ở hai chỗ: tiêu đề gọi theo tên vòng (tứ kết / bán kết / chung
 * kết) chứ không phải số vòng, và không có nhãn "đang diễn ra / đã đóng" vì nhánh
 * không có khái niệm vòng mở — cứ đủ hai người là đánh được.
 */
const PlayoffGroup = ({
  round,
  defaultOpen,
  filter,
  myPlayerId,
  openRounds,
  toggle,
}: {
  round: SwissPlayoffRound;
  defaultOpen: boolean;
  filter: "all" | "mine";
  myPlayerId: string | null;
  openRounds: Record<string, boolean>;
  toggle: (key: string) => void;
}) => {
  const matches = filter === "mine" ? round.matches.filter((m) => m.mine) : round.matches;
  const key = "po" + round.round;
  const open = openRounds[key] !== undefined ? openRounds[key] : defaultOpen;
  if (filter === "mine" && matches.length === 0) {
    return null;
  }
  const done = matches.filter((m) => m.status === "CONFIRMED").length;

  return (
    <div className={`roundgrp ${open ? "" : "closed"}`}>
      <div className="rh" onClick={() => toggle(key)}>
        <span className="rt">{round.name}</span>
        <span className="rc">
          {done}/{matches.length} trận {open ? "▾" : "▸"}
        </span>
      </div>
      <div className="rb">
        {matches.map((match) => (
          <MatchRow key={match.scheduleId} match={match} myPlayerId={myPlayerId} />
        ))}
      </div>
    </div>
  );
};

/**
 * Sơ đồ nhánh loại trực tiếp.
 *
 * Mỗi vòng là một cột, các cặp trong cột dàn đều theo chiều dọc nên cặp vòng sau
 * nằm ngang tầm giữa hai cặp đã sinh ra nó — đúng cách đọc một bracket. Cột cuộn
 * ngang thay vì thu nhỏ chữ: nhánh top 16 mà ép vừa bề ngang điện thoại thì không
 * còn đọc được tên ai.
 */
const PlayoffBracket = ({
  roundList,
  myPlayerId,
}: {
  roundList: SwissPlayoffRound[];
  myPlayerId: string | null;
}) => {
  /* Chỉ trận cuối cùng mới sinh ra nhà vô địch. Người thắng bán kết cũng mang cờ
     isWinner, gắn cúp theo riêng cờ đó thì nửa sơ đồ đầy cúp. Kiểm cả số trận vì
     một nhánh dựng dở (chưa đủ vòng) có cột cuối nhiều hơn một cặp. */
  const cuoiCung = roundList[roundList.length - 1];
  const chungKet = cuoiCung && cuoiCung.matches.length === 1 ? cuoiCung : null;

  return (
  <div className="bracket">
    {roundList.map((round) => (
      <div className="bcol" key={round.round}>
        <div className="bch">{round.name}</div>
        <div className="bcb">
          {round.matches.map((match, index) => (
            /* Mỗi cặp nằm trong một ô cao bằng nhau (.bslot flex:1), nên tâm cặp ở
               vòng sau rơi đúng vào ranh giới hai ô của vòng trước — đó là điều kiện
               để mấy đoạn nối vẽ bằng CSS ăn khớp. Xem .bslot trong shared/league.css. */
            <div className={`bslot ${index % 2 === 0 ? "top" : "bottom"}`} key={match.scheduleId}>
              <BracketNode match={match} myPlayerId={myPlayerId} laChungKet={round === chungKet} />
              <i className="c-out" />
              <i className="c-join" />
              <i className="c-in" />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
  );
};

export default function SwissLeagueLive({ leagueId }: { leagueId: string }) {
  const user = useRecoilValue(userState);
  const navigate = useNavigate();

  const [view, setView] = useState<SwissView | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  const [tab, setTab] = useState<Tab>("info");
  /* "Của bạn" chỉ có nghĩa khi người dùng thật sự được thi đấu; mặc định an
     toàn là "Tất cả", rồi đổi sang "Của bạn" khi biết họ đã vào giải chính
     thức (xem effect bên dưới). */
  const [filter, setFilter] = useState<"all" | "mine">("all");
  const [venue, setVenue] = useState<string | null>(null);
  const [openRounds, setOpenRounds] = useState<Record<string, boolean>>({});
  const [sheetOpen, setSheetOpen] = useState(false);
  const [confirmSheetOpen, setConfirmSheetOpen] = useState(false);
  const [sheetScore, setSheetScore] = useState<{ me: number; opp: number }>({ me: 0, opp: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [rankSub, setRankSub] = useState<"group" | "playoff">("group");
  const [anhPhongTo, setAnhPhongTo] = useState<AnhPhongTo | null>(null);
  const [feeModalOpen, setFeeModalOpen] = useState(false);

  const playerId = user?.id || null;

  // load() được tạo lại theo [leagueId, playerId] nên biến state đọc trong đó có
  // thể cũ; dùng ref để biết chắc màn hình đã có dữ liệu hay chưa.
  const hasView = useRef(false);

  const load = useCallback(async () => {
    try {
      const next = await fetchSwissView(leagueId, playerId);
      setView(next);
      hasView.current = true;
      setFailed(false);
      if (!venue && next.me.venueId) {
        setVenue(next.me.venueId);
      }
    } catch (e: any) {
      // Đã có dữ liệu trên màn hình thì giữ nguyên, chỉ báo lỗi lần nạp này -
      // đừng thay cả trang bằng "Không tìm thấy giải Swiss này".
      setFailed(!hasView.current);
      if (e?.response?.data?.error) {
        toast.error("Lỗi, " + e.response.data.error);
      } else {
        // Kèm mã HTTP để người test nói ngay được là hỏng ở đâu.
        const status = e?.response?.status;
        toast.error("Không tải được giải đấu" + (status ? ` (HTTP ${status})` : ""));
      }
    } finally {
      setLoading(false);
    }
    // `venue` chỉ dùng để đặt giá trị mặc định lần đầu, không cần nạp lại khi đổi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId, playerId]);

  useEffect(() => {
    load();
  }, [load]);

  /* Ai đó ở ngoài trang này vừa làm đổi trạng thái - cụ thể là hộp thoại thách đấu
     treo ở gốc app. Nó không dựng lại được trang này bằng điều hướng, nên báo bằng
     một con số tăng dần. */
  const nhipNapLai = useRecoilValue(swissRefreshState);
  useEffect(() => {
    if (nhipNapLai > 0) {
      load();
    }
  }, [nhipNapLai, load]);

  /* Trạng thái trận đổi được mà KHÔNG do người đang xem làm: đối thủ nộp tỉ số, hoặc
     ban tổ chức sửa kết quả bên back office. Không có đường realtime nào cho hai việc
     đó - Firebase chỉ được ghi bởi máy của người quét mã QR, còn back office thì
     không nói chuyện với nó - nên tự nạp lại theo nhịp.
     
     CHỈ NẠP KHI TAB ĐANG HIỆN. Người để app chạy nền cả buổi mà vẫn gọi API 20 giây
     một lần là đốt pin và đốt băng thông cho một màn hình không ai nhìn. Quay lại tab
     thì nạp ngay một lần, vì đó đúng là lúc người ta muốn thấy số mới nhất. */
  useEffect(() => {
    const dangXem = () => document.visibilityState === "visible";
    const dinhKy = setInterval(() => {
      if (dangXem()) {
        load();
      }
    }, 20000);
    const khiQuayLai = () => {
      if (dangXem()) {
        load();
      }
    };
    document.addEventListener("visibilitychange", khiQuayLai);

    return () => {
      clearInterval(dinhKy);
      document.removeEventListener("visibilitychange", khiQuayLai);
    };
  }, [load]);

  const league = view?.league;
  const banner = getAssetFullUrl(league?.banner);
  const playoff = view?.playoff;
  const me = view?.me;
  const joined = !!me?.registered;

  /* GIẢI NÀY CÓ VÒNG LOẠI TRỰC TIẾP HAY KHÔNG - đọc từ cờ "Cách giải kết thúc", không
     còn đọc từ "đã bốc nhánh chưa" như trước.

     Khác nhau ở khoảng giữa, và khoảng giữa đó là phần lớn đời một giải: từ lúc mở đăng
     ký tới lúc người trực giải chốt vòng bảng, `drawn` luôn false. Trang vì vậy im lặng
     suốt giai đoạn cơ thủ cần biết nhất - họ đăng ký một giải mà không biết nó kết thúc
     bằng một nhánh loại trực tiếp hay bằng người cuối cùng còn mạng.

     `|| drawn` giữ lại giải đã cắt top từ trước khi có cờ: chúng đọc ra "het-mang" vì
     không có khoá nào trong sub_data, mà nhánh thì có thật. api.ts dùng đúng điều kiện
     này để dọn `playoff`/`me.playoff`, nên ở đây nó chỉ còn là cái tên để đọc cho rõ. */
  const coNhanh = league?.playoffMode === "cat-top" || !!playoff?.drawn;

  const visibleRounds = useMemo(() => (view ? [...view.rounds].reverse() : []), [view]);

  /* Giải đã bốc nhánh thì vòng bảng là chuyện đã xong: mở tab BXH ra phải thấy ngay
     vòng loại trực tiếp, không phải bấm thêm một nhịp nữa.

     Chỉ tự chọn ĐÚNG MỘT LẦN cho mỗi lần vào trang. Để nó chạy theo mọi lượt tải lại
     dữ liệu thì cứ mỗi nhịp poll là người đang xem vòng bảng lại bị kéo về nhánh —
     tự động một lần là tiện, tự động mãi là giành quyền điều khiển của người dùng. */
  const daTuChonNhanh = useRef(false);
  useEffect(() => {
    if (playoff?.drawn && !daTuChonNhanh.current) {
      daTuChonNhanh.current = true;
      setRankSub("playoff");
    }
  }, [playoff?.drawn]);

  /* Số cơ thủ còn trong nhánh: người vào vòng 1 trừ đi ai đã thua một trận nhánh.
     Không suy từ số vòng đã xong — nhánh chạy không đều, bán kết có thể xong một cặp
     trong khi cặp kia còn đang đấu. */
  const playoffAlive = useMemo(() => {
    const list = view?.playoff?.roundList ?? [];
    if (list.length === 0) return 0;
    const entrants = new Set<string>();
    for (const match of list[0].matches) {
      for (const player of match.players) {
        if (player.id) entrants.add(player.id);
      }
    }
    for (const round of list) {
      for (const match of round.matches) {
        if (match.status !== "CONFIRMED") continue;
        for (const player of match.players) {
          if (player.id && !player.isWinner) entrants.delete(player.id);
        }
      }
    }
    return entrants.size;
  }, [view]);

  /* Thắng–thua TRONG NHÁNH của một cơ thủ. Vòng bảng và nhánh là hai giai đoạn
     khác nhau nên hai bộ số phải tách: 1–2 ở vòng bảng vẫn có thể là 2–0 ở nhánh. */
  const playoffRecord = useCallback(
    (who: string | null | undefined) => {
      const list = view?.playoff?.roundList ?? [];
      let wins = 0;
      let losses = 0;
      if (!who) return { wins, losses };
      for (const round of list) {
        for (const match of round.matches) {
          if (match.status !== "CONFIRMED") continue;
          const player = match.players.find((x) => x.id === who);
          if (!player) continue;
          if (player.isWinner) wins++;
          else losses++;
        }
      }
      return { wins, losses };
    },
    [view]
  );

  const selectedVenue: SwissVenue | null = useMemo(() => {
    if (!view) return null;
    const id = joined ? me?.venueId : venue;
    return view.venues.find((v) => v.id === id) || null;
  }, [view, venue, joined, me]);

  // Đã đăng ký → mặc định vào tab "Thi đấu" (một lần), sau đó người dùng tự điều hướng.
  const joinedTabInit = useRef(false);
  useEffect(() => {
    if (joined && !joinedTabInit.current) {
      joinedTabInit.current = true;
      setTab("play");
    }
  }, [joined]);

  /* Bộ lọc danh sách vòng, đặt một lần khi biết tình trạng của người xem.
     Chưa vào giải chính thức — chưa đăng ký, hoặc đăng ký rồi mà BTC chưa xác
     nhận đóng phí — thì "Của bạn" luôn rỗng; mở sẵn "Tất cả" để họ còn xem
     được giải đang diễn ra thế nào. */
  const filterInit = useRef(false);
  useEffect(() => {
    if (!view || filterInit.current) return;
    filterInit.current = true;
    const canPlay = !!me?.registered && me.state !== "not_joined" && me.state !== "waiting_fee";
    setFilter(canPlay ? "mine" : "all");
  }, [view, me]);

  const standings = view?.standings ?? null;

  const toggle = (key: string) =>
    setOpenRounds((state) => {
      const current = state[key] !== undefined ? state[key] : key === "r" + (visibleRounds[0]?.round ?? 0);
      return { ...state, [key]: !current };
    });

  const handleJoin = async () => {
    if (!league) return;
    if (!playerId) {
      toast.error("Vui lòng đăng nhập để đăng ký giải");
      return navigate("/login");
    }
    setBusy(true);
    try {
      await SBService.joinSwissLeague(league.id);
      toast.success(league.hasFee ? "Đăng ký thành công, chờ xác nhận đóng phí" : "Đăng ký thành công");
      setModalOpen(false);
      setTab("play");
      await load();
    } catch (e: any) {
      const code = e?.response?.data?.error;
      const messages: Record<string, string> = {
        venueRequired: "Vui lòng chọn địa điểm thi đấu",
        venueFull: "Địa điểm đã hết suất",
        venueNotInLeague: "Địa điểm không thuộc giải này",
        leagueFull: "Giải đã đủ số cơ thủ",
        leagueAlreadyStarted: "Giải đã bốc cặp, không nhận thêm đăng ký",
        registrationClosed: "Đã hết hạn đăng ký",
        alreadyJoinedLeague: "Bạn đã đăng ký giải này",
        playerNotCalibrated: "Bạn cần được xếp hạng trước khi đăng ký",
        playerRankIsNotAllowed: "Hạng của bạn không thuộc nhóm được tham gia",
      };
      toast.error(messages[code] || "Không đăng ký được, vui lòng thử lại");
    } finally {
      setBusy(false);
    }
  };

  /** Tỉ số đang chờ xác nhận, quy về góc nhìn của tôi. */
  const pendingScore = (current: SwissCurrentMatch | null | undefined) => {
    const pending = current?.pendingResult;
    if (!pending || !playerId) return null;
    const iAmChallenger = current?.challengerId === playerId;
    return {
      me: Number((iAmChallenger ? pending.challengerPoint : pending.acceptorPoint) ?? 0),
      opp: Number((iAmChallenger ? pending.acceptorPoint : pending.challengerPoint) ?? 0),
    };
  };

  const openResultSheet = () => {
    const current = me?.currentMatch;
    if (!current || !league) return;
    /* Đã có tỉ số chờ xác nhận thì mở sheet với đúng tỉ số đó — dù là của mình
       (sửa lại) hay của đối thủ (nhập tỉ số khác). Trước đây luôn reset về 0–0,
       nên người sửa phải bấm lại từ đầu và dễ gửi nhầm.
       Chưa có gì thì vẫn bắt đầu từ 0 để không submit nhầm điểm mặc định. */
    setSheetScore(pendingScore(current) ?? { me: 0, opp: 0 });
    setSheetOpen(true);
  };

  const handleSubmitResult = async () => {
    const current = me?.currentMatch;
    if (!current?.matchId || !league || !playerId) return;
    const iAmChallenger = current.challengerId === playerId;
    setBusy(true);
    try {
      await SBService.submitMatch(current.matchId, {
        challengerPoint: iAmChallenger ? sheetScore.me : sheetScore.opp,
        acceptorPoint: iAmChallenger ? sheetScore.opp : sheetScore.me,
        challengerId: current.challengerId,
        acceptorId: current.acceptorId,
        billPhoto: null,
        billAmount: 0,
      });
      toast.success("Đã gửi kết quả, chờ đối thủ xác nhận");
      setSheetOpen(false);
      await load();
    } catch (e: any) {
      const code = e?.response?.data?.error;
      if (code === "pointNotMatchedToRace") {
        toast.error("Tỉ số chưa đủ chạm " + (current.race || league.race));
      } else {
        toast.error("Không gửi được kết quả");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmResult = async () => {
    const current = me?.currentMatch;
    if (!current?.matchId || !current.pendingResult) return;
    setBusy(true);
    try {
      await SBService.confirmResult(
        current.matchId,
        current.pendingResult.id,
        current.challengerId || "",
        current.acceptorId || ""
      );
      toast.success("Đã xác nhận kết quả");
      setConfirmSheetOpen(false);
      await load();
    } catch (e: any) {
      /* Nuốt mã lỗi khiến mọi nguyên nhân trông giống nhau và không debug được.
         Mã nào biết thì dịch, mã lạ thì hiện nguyên văn để còn lần ra. */
      const code = e?.response?.data?.error;
      const messages: Record<string, string> = {
        matchNotFound: "Không tìm thấy trận hoặc kết quả cần xác nhận",
        matchNotAcceptedOrSubmitted: "Trận chưa ở trạng thái chờ xác nhận",
        acceptorIdNotMatched: "Dữ liệu trận không khớp, vui lòng tải lại trang",
        challengerIdNotMatched: "Dữ liệu trận không khớp, vui lòng tải lại trang",
        notPlayerInMatch: "Bạn không thuộc trận này",
        playersNotFoundInLeague: "Thiếu dữ liệu đăng ký của cơ thủ trong giải",
        playersIsNotValid: "Cơ thủ chưa có hạng nên chưa tính được điểm",
      };
      toast.error(messages[code] || (code ? `Không xác nhận được kết quả (${code})` : "Không xác nhận được kết quả"));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Nhận lời thách đấu của đối thủ.
   *
   * Không có endpoint riêng: bên nào cũng gọi cùng một API thách đấu, backend coi
   * ai gọi sau là người đồng ý. Nhờ vậy "bấm xác nhận" ở đây và "quét lại mã của
   * họ" là hai đường dẫn tới cùng một kết quả.
   */
  const handleAcceptChallenge = async () => {
    const opponentId = me?.currentMatch?.opponent?.playerId;
    if (!league || !opponentId) return;
    setBusy(true);
    try {
      await SBService.swissChallenge(league.id, opponentId);
      toast.success("Đã xác nhận, mời nhập kết quả sau khi đánh xong");
      await load();
    } catch (e: any) {
      const code = e?.response?.data?.error;
      const messages: Record<string, string> = {
        notPairedThisRound: "Người này không phải đối thủ của bạn ở vòng hiện tại",
        matchAlreadyFinished: "Trận này đã có kết quả",
        feeNotPaid: "Cần được xác nhận đóng phí trước khi thi đấu",
      };
      toast.error(messages[code] || "Không xác nhận được, vui lòng thử lại");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Từ chối lời mời: trận trở lại "chưa ai quét", mời lại được từ đầu.
   *
   * Báo ngược cho người mời qua Firebase để họ khỏi ngồi chờ một lời mời đã bị
   * từ chối. Ghi hỏng thì thôi — họ vẫn thấy đúng trạng thái khi mở lại trang.
   */
  const handleDeclineChallenge = async () => {
    const opponentId = me?.currentMatch?.opponent?.playerId;
    if (!league || !opponentId) return;
    setBusy(true);
    try {
      await SBService.swissDeclineChallenge(league.id, opponentId);
      await pingChallengeDeclined(opponentId, league.id, user?.name || "");
      toast.success("Đã từ chối lời thách đấu");
      await load();
    } catch (e: any) {
      const code = e?.response?.data?.error;
      const messages: Record<string, string> = {
        noChallengeToDecline: "Lời mời này không còn nữa",
        cannotDeclineOwnChallenge: "Đây là lời mời của chính bạn",
        matchAlreadyStarted: "Trận đã bắt đầu, không từ chối được nữa",
      };
      toast.error(messages[code] || "Không từ chối được, vui lòng thử lại");
    } finally {
      setBusy(false);
    }
  };

  const handleCheckin = async () => {
    if (!league) return;
    setBusy(true);
    try {
      await SBService.checkinLeagueById(league.id);
      toast.success("Check-in thành công");
      await load();
    } catch (e: any) {
      toast.error("Không check-in được");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="biadi">
        <BilliardLoader variant="center" label="Đang tải giải đấu…" />
      </div>
    );
  }

  if (failed || !view || !league || !me) {
    return (
      <div className="biadi">
        <div className="shell">
          <div className="pad">
            <div className="emptybox">Không tìm thấy giải Swiss này.</div>
          </div>
        </div>
      </div>
    );
  }

  /* Giai đoạn của giải suy theo mốc thời gian cấu hình (ưu tiên ngày hơn cờ round
     của BE): đăng ký → hết hạn ĐK → ghép cặp/thi đấu → kết thúc. */
  const nowM = moment();
  const hasTimeline = !!league.startPairingDate || !!league.startDate;
  const startedByDate =
    (!!league.startPairingDate && nowM.isSameOrAfter(moment(league.startPairingDate))) ||
    (!!league.startDate && nowM.isSameOrAfter(moment(league.startDate)));
  // Cờ trạng thái thực từ BE: giải đã bốc cặp (swissCurrentRound > 0 / có vòng đang mở).
  // Bốc cặp là thao tác thủ công của admin, KHÔNG gắn với startPairingDate — nên có thể
  // xảy ra trước ngày cấu hình. API join khoá đăng ký theo cờ này, không theo ngày.
  const pairedByState = league.currentRound > 0 || !!league.openRound;
  // Trạng thái/pha hiển thị vẫn ưu tiên NGÀY cấu hình (đăng ký → ghép cặp → thi đấu).
  const started = hasTimeline ? startedByDate : pairedByState;
  const finished =
    league.isFinished || (!!league.endDate && nowM.isAfter(moment(league.endDate).endOf("day")));
  const beforeReg = !!league.startRegDate && nowM.isBefore(moment(league.startRegDate));
  const regClosed = !!league.endRegDate && nowM.isAfter(moment(league.endRegDate).endOf("day"));
  // Đăng ký chỉ khoá theo KHUNG ĐĂNG KÝ và việc đã bốc cặp thật, KHÔNG theo ngày bắt đầu
  // giải / ngày ghép cặp: giải một ngày phải đăng ký được trong ngày thi đấu miễn khung
  // đăng ký còn mở. API join cũng khoá theo cờ bốc cặp, không theo ngày.
  const canRegister = !finished && !beforeReg && !regClosed && !pairedByState;
  const regCta = finished
    ? "Giải đã kết thúc"
    : pairedByState
      ? "Giải đã bốc cặp"
      : beforeReg
        ? "Chưa mở đăng ký"
        : regClosed
          ? "Đã hết hạn đăng ký"
          : "Tham gia ngay";

  /* Đã đăng ký, chờ đóng phí, nhưng giải đã bắt đầu và không còn nhận đăng ký nữa
     (hết hạn đăng ký / đã bốc cặp / đã kết thúc) → đăng ký không thành công. */
  const feeFailed = me.state === "waiting_fee" && (finished || pairedByState || (started && regClosed));

  /* ------------------------------ Khối dùng lại ------------------------------ */

  const Navbar = ({ brand = true }: { brand?: boolean }) => (
    <div className="navbar">
      <button className="back" onClick={() => navigate(-1)}>
        ‹
      </button>
      <span className="ttl">Thông tin giải đấu</span>
      {brand && (
        <span className="brandmark">
          <span className="bmk">B</span>BIADI
        </span>
      )}
    </div>
  );

  const TabPrize = () => (
    <div className="sec">
      {view.prizes.length === 0 ? (
        <div className="tiny">Ban tổ chức chưa công bố cơ cấu giải thưởng.</div>
      ) : (
        <>
          <div className="prizehead">
            <span>Thứ hạng</span>
            <span style={{ textAlign: "right" }}>Tiền mặt</span>
            <span>Hiện vật</span>
          </div>
          {view.prizes.map((prize, index) => (
            <div key={index} className="prizerow">
              <span className="p1">{prize.label}</span>
              <span className="p2">{prize.cash ? `${money(prize.cash)} đ` : "—"}</span>
              <span className="small">{prize.kind || "—"}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );

  const TabRule = () => (
    <div className="sec rulebody">
      <div className="metarow" style={{ marginBottom: 10 }}>
        <span className="chip solid">Swiss</span>
        <span className="dot" />
        <span>{league.lives} mạng</span>
        <span className="dot" />
        <span>Chạm {league.race}</span>
        {league.est && (
          <>
            <span className="dot" />
            <span>dự kiến {league.est} vòng</span>
          </>
        )}
      </div>
      {/* CÁCH GIẢI KẾT THÚC, nói thành câu chứ không nhét vào hàng chip phía trên: đây
          là thứ đổi hẳn hình dạng của giải, không phải một thông số đứng ngang với
          "chạm 5". Cơ thủ đọc tab này lúc cân nhắc đăng ký, và trước đây cả trang
          không có chỗ nào trả lời được "giải này kết thúc kiểu gì". */}
      <div className="tiny" style={{ marginBottom: 10 }}>
        {coNhanh ? (
          <>
            Giải đánh vòng loại Swiss rồi <b style={{ color: "var(--chalk)" }}>cắt top vào vòng loại trực tiếp</b>
            {playoff?.size ? ` (top ${playoff.size})` : ""}. Vào nhánh rồi thì thua một trận là dừng bước, không còn
            tính mạng. Vô địch là người thắng trận chung kết.
          </>
        ) : (
          <>
            Giải <b style={{ color: "var(--chalk)" }}>không có vòng loại trực tiếp</b>: đánh Swiss tới khi chỉ còn 1
            cơ thủ còn mạng, người đó đứng hạng 1 và vô địch.
          </>
        )}
      </div>
      {/* Thể lệ & luật thi đấu lấy từ admin (không hardcode). */}
      {league.rule ? (
        <div className="raw-css" dangerouslySetInnerHTML={{ __html: league.rule }} />
      ) : (
        <div className="tiny">Ban tổ chức chưa công bố thể lệ chi tiết.</div>
      )}
      {league.fee ? (
        <div className="tiny" style={{ marginTop: 10 }}>
          Lệ phí tham gia <b>{money(league.fee)} đ</b>
          {league.feeQrImage || league.feeTransferNote
            ? ", chuyển khoản theo mã QR ở ô Đóng phí."
            : ", đóng tại địa điểm khi check-in."}
        </div>
      ) : null}
    </div>
  );

  const TabRegistered = () => (
    <div className="sec" style={{ padding: "18px 12px", textAlign: "center" }}>
      <div className="tiny">
        {league.registered > 0
          ? `${league.registered}/${league.players || league.registered} cơ thủ đã đăng ký`
          : "Chưa có cơ thủ nào đăng ký"}
      </div>
    </div>
  );

  const StatusAction = () => {
    const current = me.currentMatch;
    const opponent = current?.opponent;
    /* Ở nhánh loại trực tiếp không còn mạng để đếm, và thắng–thua vòng bảng thuộc
       về một giai đoạn đã khép lại. Dòng phụ đổi sang thành tích trong chính nhánh. */
    const inPlayoff = !!playoff?.drawn;
    const myPo = playoffRecord(playerId);
    const oppPo = playoffRecord(opponent?.playerId);
    /* Hạng và điểm xếp hạng thay cho thắng–thua–mạng: trước khi vào trận, thứ người
       ta muốn biết về đối thủ là họ mạnh cỡ nào, không phải họ đã đá mấy trận ở giải
       này. Thiếu dữ liệu hạng thì không in một dấu gạch trống mà lùi về thành tích
       cũ - vẫn hơn một dòng rỗng. */
    const trinhDo = (rankClass: string | null, score: number | null, duPhong: JSX.Element) =>
      rankClass || score !== null ? (
        <>
          {rankClass ? <b style={{ color: "var(--chalk)" }}>{rankClass}</b> : null}
          {rankClass && score !== null ? " • " : null}
          {score !== null ? `${score.toLocaleString("vi-VN")} điểm` : null}
        </>
      ) : (
        duPhong
      );

    const opponentBlock = opponent && (
      <div className="opp">
        <Avatar
          src={me.avatar}
          name={user?.name || ""}
          me
          rankClass={me.rankClass}
          score={me.score}
          onZoom={setAnhPhongTo}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="nm">Bạn</div>
          <div className="tiny">
            {trinhDo(
              me.rankClass,
              me.score,
              inPlayoff ? (
                <>
                  {myPo.wins}–{myPo.losses} ở nhánh
                </>
              ) : (
                <>
                  {me.wins}–{me.losses} • {me.lives} mạng
                </>
              ),
            )}
          </div>
        </div>
        <div className="vsbadge">VS</div>
        <div style={{ flex: 1, minWidth: 0, textAlign: "right" }}>
          <div className="nm">{opponent.name}</div>
          <div className="tiny">
            {trinhDo(
              opponent.rankClass,
              opponent.score,
              inPlayoff ? (
                <>
                  {oppPo.wins}–{oppPo.losses} ở nhánh
                </>
              ) : (
                <>
                  {opponent.wins}–{opponent.losses} • {opponent.lives} mạng
                </>
              ),
            )}
          </div>
        </div>
        <Avatar
          src={opponent.avatar}
          name={opponent.name}
          rankClass={opponent.rankClass}
          score={opponent.score}
          onZoom={setAnhPhongTo}
        />
      </div>
    );

    switch (me.state) {
      case "waiting_start":
        return (
          <>
            <div className="kicker">Chờ BTC bắt đầu vòng 1</div>
            <div className="expl">
              Bạn đã có suất{selectedVenue ? <> tại <b style={{ color: "var(--chalk)" }}>{selectedVenue.name}</b></> : null}. BTC
              sẽ bốc cặp vòng 1 sau khi đóng check-in.
            </div>
            <div className="btns">
              <button className="btn btn-primary" disabled={busy || me.isCheckin} onClick={handleCheckin}>
                {me.isCheckin ? "Đã check-in" : "Check-in tại địa điểm"}
              </button>
              <button className="btn btn-quiet" onClick={() => setTab("info")}>
                Xem thể thức
              </button>
            </div>
          </>
        );
      case "has_match": {
        const playoffRoundName = me.playoff?.stage === "playoff" ? me.playoff.roundName : null;
        /* Trận được back office tạo sẵn lúc bốc cặp (nên matchId có từ đầu),
           nhưng ở trạng thái WAITING. Chỉ khi một trong hai người quét QR của
           người kia và bấm "Thách đấu" thì trận mới sang ACCEPTED — cũng là lúc
           API mới nhận tỉ số. */
        const started = !!current?.started;
        const challenge = current?.challenge ?? null;

        /* Đã có lời mời nhưng trận chưa bắt đầu: một bên đã quét mã, đang chờ bên
           kia gật đầu. Người được mời xác nhận bằng chính lời thách đấu ngược lại:
           backend coi ai gọi sau là người đồng ý. */
        if (!started && challenge) {
          return (
            <>
              <div className="kicker">
                {challenge.byMe ? "Đang chờ đối thủ xác nhận" : `${opponent?.name || "Đối thủ"} muốn bắt đầu trận`}
              </div>
              {opponentBlock}
              <div className="expl">
                {challenge.byMe
                  ? "Bạn đã quét mã của đối thủ. Trận chỉ bắt đầu, và mở nhập kết quả, sau khi họ xác nhận."
                  : "Họ đã quét mã QR của bạn. Xác nhận để bắt đầu trận; sau đó cả hai mới nhập được tỉ số."}
              </div>
              <div className="btns">
                {challenge.byMe ? (
                  <button className="btn btn-ghost" onClick={() => navigate("/my-qr")}>
                    QR của tôi
                  </button>
                ) : (
                  <>
                    <button className="btn btn-primary" disabled={busy} onClick={handleAcceptChallenge}>
                      Xác nhận thi đấu
                    </button>
                    <button className="btn btn-ghost" disabled={busy} onClick={handleDeclineChallenge}>
                      Từ chối
                    </button>
                  </>
                )}
              </div>
            </>
          );
        }

        return (
          <>
            <div className="kicker">
              {playoffRoundName ? `Bạn có trận ${playoffRoundName.toLowerCase()}` : `Bạn có trận ở vòng ${me.currentRound}`}
            </div>
            {opponentBlock}
            {started ? (
              <div className="btns">
                <button className="btn btn-primary" onClick={openResultSheet}>
                  Nhập kết quả
                </button>
              </div>
            ) : (
              <>
                <div className="expl">
                  Gặp đối thủ tại bàn rồi đưa mã QR của bạn cho họ quét — hoặc quét mã của họ — để
                  bắt đầu trận. Nhập kết quả mở ra ngay sau đó.
                </div>
                <div className="btns">
                  <button className="btn btn-primary" onClick={() => navigate("/my-qr")}>
                    QR của tôi
                  </button>
                  <button className="btn btn-ghost" onClick={() => navigate(`/scan-qr/${league.id}`)}>
                    Quét QR đối thủ
                  </button>
                </div>
              </>
            )}
          </>
        );
      }
      case "waiting_confirm": {
        const pending = current?.pendingResult;
        const mineSubmitted = pending?.submittedByMe;
        /* Tỉ số đang chờ xác nhận là thứ duy nhất cần nhìn ở màn này: người gửi
           phải kiểm lại được cái mình đã gửi, người xác nhận phải thấy con số
           mình sắp đồng ý. Khối đối thủ bên dưới chỉ là thắng–thua toàn giải. */
        const score = pendingScore(current);
        return (
          <>
            <div className="kicker">{mineSubmitted ? "Chờ đối thủ xác nhận" : "Đối thủ đã nhập kết quả"}</div>
            {score ? (
              <div className="pendingscore">
                <span className="ps-side">Bạn</span>
                <span className="ps-val">
                  {score.me}<span className="ps-sep">–</span>{score.opp}
                </span>
                <span className="ps-side ps-right">{current?.opponent?.name || "Đối thủ"}</span>
              </div>
            ) : null}
            <div className="expl">
              {mineSubmitted
                ? "Kết quả chỉ được tính sau khi đối thủ xác nhận."
                : "Kiểm tra tỉ số đối thủ đã nhập rồi xác nhận. Sau khi xác nhận, trận được tính vào rating Biadi và không sửa được."}
            </div>
            {opponentBlock}
            <div className="btns">
              {mineSubmitted ? (
                <button className="btn btn-ghost" onClick={openResultSheet} disabled={busy}>
                  Sửa kết quả
                </button>
              ) : (
                <>
                  <button className="btn btn-primary" onClick={() => setConfirmSheetOpen(true)} disabled={busy}>
                    Xác nhận kết quả
                  </button>
                  <button className="btn btn-ghost" onClick={openResultSheet} disabled={busy}>
                    Nhập tỉ số khác
                  </button>
                </>
              )}
            </div>
          </>
        );
      }
      case "playoff_waiting":
        return (
          <>
            <div className="kicker">
              {me.playoff?.roundName ? `Bạn vào ${me.playoff.roundName.toLowerCase()}` : "Bạn đi tiếp"}
            </div>
            <div className="expl">
              Đối thủ của bạn là người thắng của cặp còn lại, chưa đấu xong. Sơ đồ nhánh bên dưới cập nhật
              ngay khi cặp đó có kết quả.
            </div>
            <div className="btns">
              <button className="btn btn-ghost" onClick={() => setTab("rank")}>
                Xem thứ hạng
              </button>
            </div>
          </>
        );
      case "playoff_out":
        return (
          <>
            <div className="kicker">
              {me.playoff?.inBracket ? "Bạn đã dừng bước ở vòng loại trực tiếp" : "Bạn không vào vòng loại trực tiếp"}
            </div>
            <div className="expl">
              {me.playoff?.inBracket ? (
                <>
                  {me.playoff.note ? `${me.playoff.note}. ` : ""}
                  Thứ hạng chung cuộc của bạn là <b style={{ color: "var(--chalk)" }}>hạng {me.playoff.rank}</b>.
                </>
              ) : (
                <>
                  Giải lấy top {me.playoff?.size} của vòng bảng vào nhánh loại trực tiếp. Bạn kết thúc giải ở{" "}
                  <b style={{ color: "var(--chalk)" }}>hạng {me.playoff?.rank ?? me.rank}</b>.
                </>
              )}
            </div>
            <div className="btns">
              <button className="btn btn-ghost" onClick={() => setTab("rank")}>
                Xem thứ hạng chung cuộc
              </button>
            </div>
          </>
        );
      case "match_done":
        return (
          <>
            <div className="kicker">
              Đã xác nhận • {current?.myScore}–{current?.opponentScore}
            </div>
            <div className="expl">
              Trận đã tính vào rating Biadi. Bạn còn <b style={{ color: "var(--chalk)" }}>{me.lives} mạng</b> và ở nhóm{" "}
              {me.losses} thua. BXH sẽ chốt khi vòng đóng.
            </div>
            <div className="btns">
              <button className="btn btn-ghost" onClick={() => setTab("rank")}>
                Xem BXH tạm tính
              </button>
            </div>
          </>
        );
      case "waiting_pairing":
        return (
          <>
            <div className="kicker">Chờ bốc cặp vòng {me.closedRounds + 1}</div>
            <div className="expl">
              Vòng {me.closedRounds} đã đóng. Bạn đang ở nhóm {me.losses} thua với {me.lives} mạng — vòng sau bạn sẽ gặp
              người cùng nhóm.
            </div>
            <div className="btns">
              <button className="btn btn-ghost" onClick={() => setTab("rank")}>
                Xem BXH
              </button>
            </div>
          </>
        );
      case "bye":
        return (
          <>
            <div className="kicker" style={{ color: "var(--red)" }}>
              Bạn được miễn đấu vòng {me.currentRound}
            </div>
            <div className="expl">
              Do số người còn lại là số lẻ. Bạn được tính <b style={{ color: "var(--chalk)" }}>+1 thắng</b>,{" "}
              <b style={{ color: "var(--chalk)" }}>không mất mạng</b> và vào thẳng vòng tiếp theo.
            </div>
            <div className="btns">
              <button className="btn btn-quiet" onClick={() => setTab("rank")}>
                Xem BXH
              </button>
            </div>
          </>
        );
      case "eliminated":
        return (
          <>
            <div className="kicker" style={{ color: "var(--steel)" }}>
              Bạn đã dừng bước ở vòng {me.eliminatedRound}
            </div>
            <div className="expl">
              Bạn thua đủ {league.lives} trận nên hết mạng. Thành tích cuối:{" "}
              <b style={{ color: "var(--chalk)" }}>
                {me.wins} thắng – {me.losses} thua
              </b>
              .
            </div>
            <div className="btns">
              <button className="btn btn-ghost" onClick={() => setTab("rank")}>
                Xem BXH
              </button>
              <button
                className="btn btn-quiet"
                onClick={() => {
                  setFilter("mine");
                  setTab("play");
                }}
              >
                Xem lại trận của tôi
              </button>
            </div>
          </>
        );
      case "finished": {
        const champion = view.standings.rows[0];
        // Giải cắt top: hạng của tôi là hạng chung cuộc từ nhánh, không phải hạng vòng bảng.
        const myRank = me.playoff?.rank ?? me.rank;
        return (
          <>
            <div className="kicker">
              {me.playoff?.note === VO_DICH ? "Bạn vô địch giải này" : "Giải đã kết thúc"}
            </div>
            <div className="expl">
              Bạn xếp{" "}
              <b style={{ color: "var(--red)", fontFamily: "var(--ff-d)", fontSize: 17 }}>hạng {myRank ?? "—"}</b>/
              {me.totalPlayers} với {me.wins} thắng – {me.losses} thua.
              {champion ? (
                <>
                  {" "}
                  Vô địch: <b style={{ color: "var(--chalk)" }}>{champion.name}</b>.
                </>
              ) : null}
            </div>
            <div className="btns">
              <button className="btn btn-primary" onClick={() => setTab("rank")}>
                Xem BXH chung cuộc
              </button>
            </div>
          </>
        );
      }
      case "waiting_fee":
        // Giải đã bắt đầu mà BTC chưa xác nhận đóng phí → đăng ký không thành công.
        if (feeFailed) {
          return (
            <>
              <div className="kicker" style={{ color: "var(--st-dead)" }}>
                Đăng ký không thành công
              </div>
              <div className="expl">
                Giải đã bắt đầu nhưng BTC chưa xác nhận bạn đóng phí, nên bạn không được đưa vào danh sách thi đấu. Vui
                lòng liên hệ ban tổ chức để được hỗ trợ.
              </div>
              <div className="btns">
                <button className="btn btn-quiet" onClick={() => setTab("info")}>
                  Xem thông tin giải
                </button>
              </div>
            </>
          );
        }
        return (
          <>
            <div className="kicker" style={{ color: "var(--st-wait)" }}>
              Chờ ban tổ chức xác nhận đóng phí
            </div>
            <div className="expl">
              {me.fee ? (
                <>
                  Phí tham gia <b style={{ color: "var(--chalk)" }}>{me.fee.toLocaleString("vi-VN")} đ</b>
                  {league.feeQrImage || league.feeTransferNote ? (
                    <>. Bấm Đóng phí để lấy mã QR và nội dung chuyển khoản. </>
                  ) : (
                    <> — đóng tại địa điểm khi check-in. </>
                  )}
                </>
              ) : null}
              {me.billUploaded ? (
                <>
                  Đã nhận ảnh biên lai của bạn.{" "}
                </>
              ) : null}
              Bạn <b style={{ color: "var(--chalk)" }}>chưa được xếp lịch</b> cho tới khi BTC xác nhận đã đóng phí.
            </div>
            <div className="btns">
              {/* Giải cấu hình chuyển khoản mới có gì để mở; giải thu tiền mặt
                  thì ô này rỗng nên không bày nút ra. */}
              {league.feeQrImage || league.feeTransferNote ? (
                /* Nút CHÍNH, không phải nút trơn: ở trạng thái này Đóng phí là việc
                   duy nhất đưa người chơi đi tiếp - chưa đóng thì không được xếp lịch.
                   Và "btn" không kèm biến thể nào thì nền trong suốt, không viền, nên
                   nó gần như vô hình bên cạnh nút xám ngay cạnh. Giống hệt cùng ô bên
                   trang Đấu kép. */
                <button className="btn btn-primary" onClick={() => setFeeModalOpen(true)}>
                  Đóng phí
                </button>
              ) : null}
              <button className="btn btn-quiet" onClick={() => setTab("info")}>
                Xem thông tin giải
              </button>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const StatusCard = () => {
    const chipMap: Record<string, [string, string]> = {
      waiting_fee: ["chip wait", "Chờ đóng phí"],
      waiting_start: ["chip", "Chờ vòng 1"],
      has_match: ["chip live", "Có trận"],
      waiting_confirm: ["chip wait", "Chờ xác nhận"],
      match_done: ["chip done", "Đã xong vòng"],
      waiting_pairing: ["chip wait", "Chờ bốc cặp"],
      bye: ["chip red", "Miễn đấu"],
      eliminated: ["chip dead", "Bị loại"],
      finished: ["chip solid", "Kết thúc"],
      playoff_waiting: ["chip", "Chờ đối thủ"],
      playoff_out: ["chip dead", "Đã dừng bước"],
    };
    const [chipClass, chipText] = feeFailed
      ? ["chip dead", "Đăng ký thất bại"]
      : chipMap[me.state] || ["chip", "Đang diễn ra"];
    /* Đã bốc nhánh thì mọi vòng Swiss đã đóng, nên "Sau vòng 5" chỉ nói về một giai
       đoạn đã kết thúc. Lấy tên vòng nhánh làm mốc thời gian của thẻ. */
    const roundText = playoff?.drawn
      ? (me.playoff?.roundName ?? "Vòng loại trực tiếp")
      : league.openRound
        ? `Vòng ${league.openRound}`
        : league.currentRound === 0
          ? "Trước vòng 1"
          : league.isFinished
            ? `Đã xong ${league.currentRound} vòng`
            : `Sau vòng ${league.currentRound}`;

    return (
      <div className="status">
        <div className="sh">
          <span className="rnd">
            {roundText}
            {league.est && !playoff?.drawn ? (
              <>
                <span className="dot" />
                <span style={{ color: "var(--steel)", fontWeight: 600, fontSize: 11.5 }}>/{league.est} vòng</span>
              </>
            ) : null}
          </span>
          <span className={chipClass}>{chipText}</span>
        </div>
        {/* Vòng loại trực tiếp: thua là về, không còn mạng, không còn nhóm theo số
            thua, và hạng vòng bảng đã hết vai trò từ lúc bốc nhánh. Bốn ô này chỉ
            còn là bốn con số không nói lên điều gì, nên bỏ hẳn. */}
        {playoff?.drawn ? null : (
        <div className="stats4">
          <div className="st">
            <div className="k">T–B</div>
            <div className="v">
              <span style={{ color: "var(--red)" }}>{me.wins}</span>–{me.losses}
            </div>
          </div>
          <div className="st">
            <div className="k">Mạng còn</div>
            <Balls lives={me.lives} total={league.lives} />
          </div>
          <div className="st">
            <div className="k">Nhóm</div>
            <div className="v sm">
              <GroupChip losses={me.losses} dead={me.eliminatedRound !== null} />
            </div>
          </div>
          <div className="st">
            <div className="k">Hạng</div>
            <div className="v">{me.eliminatedRound !== null && !league.isFinished ? "—" : (me.rank ?? "—")}</div>
          </div>
        </div>
        )}
        <div className="action">
          <StatusAction />
        </div>
        <div className="rankline">
          <span className="rl">
            {league.isFinished
              ? "Giải đấu đã kết thúc"
              : playoff?.drawn
                ? /* Chạm của CẶP TÔI ĐANG ĐẤU (me.playoff.race), không phải con số ở cấp
                     giai đoạn: view.playoff.race chỉ là giá trị phòng hờ bằng chạm vòng
                     bảng, vì mỗi cặp một chạm nên không có số nào đúng cho cả nhánh. */
                  `Còn ${playoffAlive}/${playoff.size} cơ thủ • loại trực tiếp, chạm ${
                    me.playoff?.race ?? playoff.race
                  }`
                : /* Chưa bốc nhánh. Giải CÓ cắt top thì nói ra ngay ở đây: dòng này là
                     thứ người chơi đọc mỗi lần mở trang, và "thể thức Swiss 3 mạng"
                     một mình đọc thành "đánh tới khi hết mạng là xong" - đúng với giải
                     này thì may, sai với giải cắt top thì họ chuẩn bị sai cả giải. */
                  `Còn ${me.aliveCount}/${me.totalPlayers} cơ thủ • Swiss ${league.lives} mạng${
                    coNhanh ? ", rồi cắt top" : ", tới khi còn 1 người"
                  }`}
          </span>
          <a
            href="#top"
            onClick={(e) => {
              e.preventDefault();
              setTab("rank");
            }}
          >
            BXH ›
          </a>
        </div>
      </div>
    );
  };

  const TabPlay = () => {
    /* Giải cắt top có hai giai đoạn tách bạch: vòng bảng Swiss rồi nhánh loại trực
       tiếp. Gộp chung một danh sách thì "Vòng 1" của nhánh đứng lẫn với "Vòng 1" của
       vòng bảng và không ai đọc được. Chưa bốc nhánh thì giữ nguyên danh sách phẳng
       như trước, không thêm tiêu đề giai đoạn cho một giai đoạn duy nhất. */
    const twoStage = !!playoff?.drawn;
    const playoffGroups = (playoff?.roundList ?? [])
      .slice()
      .reverse()
      .map((round, index) => (
        <PlayoffGroup
          key={round.round}
          round={round}
          defaultOpen={index === 0}
          filter={filter}
          myPlayerId={playerId}
          openRounds={openRounds}
          toggle={toggle}
        />
      ))
      .filter(Boolean);

    const groups = visibleRounds
      .map((round, index) => (
        <RoundGroup
          key={round.round}
          round={round}
          defaultOpen={!twoStage && index === 0}
          filter={filter}
          myPlayerId={playerId}
          openRounds={openRounds}
          toggle={toggle}
        />
      ))
      .filter(Boolean);

    return (
      <div className="playgrid">
        <div className="col-left">
          <StatusCard />
        </div>
        <div className="col-right">
          <div className="mfilter">
            <div className="seg">
              <button className={filter === "all" ? "on" : ""} onClick={() => setFilter("all")}>
                Tất cả
              </button>
              <button className={filter === "mine" ? "on" : ""} onClick={() => setFilter("mine")}>
                Của bạn
              </button>
            </div>
          </div>
          {visibleRounds.length === 0 && playoffGroups.length === 0 ? (
            <div className="emptybox">Chưa có vòng nào. Danh sách trận sẽ hiện ngay khi BTC bốc cặp vòng 1.</div>
          ) : [...playoffGroups, ...groups].every((group) => group === null) ? (
            <div className="emptybox">Bạn chưa có trận nào ở bộ lọc này.</div>
          ) : (
            <>
              {twoStage && playoffGroups.some(Boolean) ? (
                <>
                  <div className="stage-h">
                    Vòng loại trực tiếp
                    <span className="tiny">Top {playoff?.size}</span>
                  </div>
                  {playoffGroups}
                </>
              ) : null}
              {twoStage && groups.some(Boolean) ? (
                <div className="stage-h">
                  Vòng bảng
                  <span className="tiny">Swiss • chạm {league.race}</span>
                </div>
              ) : null}
              {groups}
            </>
          )}
        </div>
      </div>
    );
  };

  const TabRank = () => {
    const isFinal = !!standings?.isFinal;
    const twoStage = !!playoff?.drawn;
    /* Chưa bốc nhánh thì cả màn này chỉ có một giai đoạn, không dựng thanh chọn cho
       một lựa chọn duy nhất. */
    const sub = twoStage ? rankSub : "group";

    /* Ở tab "Vòng bảng" phải là thứ tự VÒNG BẢNG, kể cả khi API đã trả thứ hạng
       chung cuộc — nếu không thì người thắng chung kết nhảy lên đầu bảng vòng bảng
       dù họ vào nhánh ở hạt giống 8. groupPosition do backend giữ lại đúng cho việc
       này, front office không tự sắp xếp lại. */
    const rows = useMemo(() => {
      const all = standings?.rows ?? [];
      if (sub !== "group" || !isFinal) {
        return all;
      }
      return [...all].sort((a, b) => a.groupPosition - b.groupPosition);
    }, [sub, isFinal]);

    const mine = rows.find((row) => row.playerId === playerId) ?? null;
    /* Cột Hạng: tab vòng bảng đọc hạng vòng bảng, tab nhánh đọc hạng chung cuộc. */
    const rankOf = (row: SwissStandingRow) =>
      sub === "group" && isFinal ? row.groupPosition : row.position;

    /* Cúp chỉ gắn ở bảng thứ hạng CHUNG CUỘC. Tab vòng bảng xếp theo groupPosition,
       mà nhà vô địch hoàn toàn có thể vào nhánh ở hạt giống 8 - gắn cúp vào dòng
       hạng 8 của vòng bảng thì đọc thành "hạng 8 vô địch". */
    const laVoDich = (row: SwissStandingRow) => sub === "playoff" && row.playoffNote === VO_DICH;

    const stageSeg = twoStage ? (
      <div className="roundchips" style={{ paddingTop: 4 }}>
        <button className={`rchip ${sub === "group" ? "on" : ""}`} onClick={() => setRankSub("group")}>
          Vòng bảng
        </button>
        <button className={`rchip ${sub === "playoff" ? "on" : ""}`} onClick={() => setRankSub("playoff")}>
          Vòng loại trực tiếp
        </button>
      </div>
    ) : null;

    const table = (
      <div className="sec" style={{ padding: "6px 10px 10px" }}>
        <table className="tbl">
          <thead>
            <tr>
              {/* 44 chứ không phải 38: từ khi bảng dùng table-layout fixed, con số này là
                ràng buộc thật, mà chữ "HẠNG" ở cỡ 10px kèm giãn chữ 1.1px đo được cần
                43px - để 38 là tiêu đề đè sang cột bên cạnh. */}
              <th style={{ width: 44 }}>Hạng</th>
              <th className="l">Cơ thủ</th>
              {sub === "playoff" ? (
                /* Nhánh loại trực tiếp: Hạng · Cơ thủ · Kết quả. Bỏ T–B để dồn chỗ
                   cho cột tên. Giữ bề rộng 116 và ô ghi chú một dòng (.ghi-chu-nhanh)
                   như develop, để "Không vào nhánh" không xuống dòng đội cao hàng. */
                <th style={{ width: 116 }}>Kết quả</th>
              ) : (
                /* Vòng bảng: Hạng · Cơ thủ · T–B · Mạng. Bỏ cột Nhóm. */
                <>
                  <th style={{ width: 56 }}>T–B</th>
                  <th style={{ width: 58 }}>Mạng</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const dead = row.eliminatedRound !== null;
              const isMe = row.playerId === playerId;
              const rank = rankOf(row);
              return (
                <tr key={row.playerId || index} className={`${isMe ? "me" : ""} ${dead ? "dead" : ""}`}>
                  <td>
                    {/* Đồng hạng là chuyện bình thường ở nhánh (hai người thua bán
                        kết cùng hạng 3), nên lấy hạng từ dữ liệu chứ không đếm dòng. */}
                    <span className="rkcell">{rank}</span>
                  </td>
                  <td className="l">
                    <div className="namecell">
                      <Avatar
                        src={row.avatar}
                        name={row.name}
                        size="avas"
                        me={isMe}
                        rankClass={row.rankClass}
                        score={row.rating}
                        onZoom={setAnhPhongTo}
                      />
                      <PlayerName name={row.name} />
                      {laVoDich(row) ? <CupVoDich /> : null}
                      {isMe && <You />}
                    </div>
                  </td>
                  {sub === "playoff" ? (
                    <td>
                      <span className="tiny ghi-chu-nhanh" title={row.playoffNote || "Không vào nhánh"}>
                        {row.playoffNote || "Không vào nhánh"}
                      </span>
                    </td>
                  ) : (
                    <>
                      <td>
                        <span className="wl">
                          <b>{row.wins}</b>–{row.losses}
                        </span>
                      </td>
                      <td>
                        {dead ? (
                          <span className="tiny">—</span>
                        ) : (
                          <div className="balls" style={{ justifyContent: "center" }}>
                            {Array.from({ length: league.lives }, (_, k) => (
                              <div
                                key={k}
                                className={`ball ${k < row.lives ? "" : "gone"}`}
                                style={{ width: 11, height: 11, fontSize: 0 }}
                              >
                                8
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );

    const myBar = mine ? (
      <div className="myrankbar">
        <span className="rkcell" style={{ color: "var(--red)" }}>
          #{rankOf(mine)}
        </span>
        <Avatar
          src={mine.avatar}
          name={mine.name}
          me
          style={{ width: 28, height: 28, fontSize: 11 }}
          rankClass={mine.rankClass}
          score={mine.rating}
          onZoom={setAnhPhongTo}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* flex chứ không để chữ trần: cúp là anh em của tên, không có flex thì nó
              dính sát vào chữ cuối cùng. */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600, fontSize: 13.5 }}>
            {mine.name}
            {laVoDich(mine) ? <CupVoDich /> : null}
          </div>
          <div className="tiny">
            {sub === "playoff"
              ? mine.playoffNote || "Không vào nhánh"
              : `${mine.wins} thắng – ${mine.losses} thua ` +
                (mine.eliminatedRound ? `• bị loại Vòng ${mine.eliminatedRound}` : `• ${mine.lives} mạng`)}
          </div>
        </div>
        <span className="chip red">Bạn</span>
      </div>
    ) : null;

    /* Sơ đồ nhánh và bảng thứ hạng chung cuộc trả lời hai câu hỏi khác nhau — ai gặp
       ai, và ai đứng thứ mấy — nên nằm cùng một tab, sơ đồ trước. */
    if (sub === "playoff") {
      return (
        <>
          {stageSeg}
          <div className="snapnote">
            Top {playoff?.size} của vòng bảng. Chạm của mỗi cặp tính theo hạng hai cơ thủ nên có thể khác nhau —
            xem ngay trên từng trận. Kéo ngang sơ đồ để xem các vòng sau. Thua bán kết là đồng hạng 3, thua tứ kết
            đồng hạng 5 — không có trận tranh hạng 3.
          </div>
          <PlayoffBracket roundList={playoff?.roundList ?? []} myPlayerId={playerId} />
          <div className="sec-h">
            <span className="label">Thứ hạng chung cuộc</span>
          </div>
          {myBar}
          {table}
        </>
      );
    }

    return (
      <>
        {stageSeg}
        {twoStage ? null : (
          <div className="sec-h">
            <span className="label">Bảng xếp hạng</span>
          </div>
        )}
        <div className="snapnote">
          {twoStage
            ? "Thứ tự vòng bảng khi chốt vòng cuối — cũng là thứ tự hạt giống vào nhánh."
            : "BXH tạm tính — cập nhật theo từng trận đã xác nhận, chốt lại khi vòng đóng."}
        </div>
        {myBar}
        {table}
        <div className="tiny" style={{ padding: "10px 2px" }}>
          Tiêu chí xếp: số trận thua ↑ <span className="dot mut" /> số trận thắng ↓ <span className="dot mut" /> điểm đối
          thủ (SoS) ↓ <span className="dot mut" /> rating Biadi ↓
        </div>
      </>
    );
  };

  const TabInfo = () => {
    /* Giải thưởng lên ĐẦU tab: đây là thứ kéo người ta bấm đăng ký, để nó nằm sau
       hai dòng ngày tháng là chôn mất phần đáng tiền nhất của trang.

       Trừ khi chưa công bố giải thưởng - lúc đó khối này chỉ còn một câu "chưa
       công bố", và mở tab ra mà câu đầu tiên là một lời từ chối thì thà để ngày
       tháng với địa điểm lên trước. Dữ liệu quyết định thứ tự, không phải bố cục
       cứng. */
    const coGiaiThuong = view.prizes.length > 0;
    const khoiGiaiThuong = (
      <>
        <div className="jhead">Giải thưởng</div>
        <TabPrize />
      </>
    );

    return (
      <>
        {coGiaiThuong ? khoiGiaiThuong : null}
        <div className="sec">
          <div className="infoline">
            <div className="ic">📅</div>
            <div>
              <div className="k">Đăng ký</div>
              <div className="v acc">{league.reg}</div>
            </div>
          </div>
          <div className="divider" />
          <div className="infoline">
            <div className="ic">🎱</div>
            <div>
              <div className="k">Thi đấu</div>
              <div className="v">{league.play}</div>
            </div>
          </div>
          <div className="divider" />
          <div className="infoline">
            <div className="ic">📍</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="k">{selectedVenue ? "Địa điểm của bạn" : "Địa điểm thi đấu"}</div>
              {selectedVenue ? (
                <>
                  <a
                    className="infolink"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      `${selectedVenue.name} ${selectedVenue.addr}`
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {selectedVenue.name}
                  </a>
                  <div className="small">{selectedVenue.addr}</div>
                  <div style={{ marginTop: 8 }}>
                    <span className="chip red">Đã cố định khi đăng ký</span>
                  </div>
                </>
              ) : view.venues.length > 0 ? (
                /* Đăng ký không còn chọn địa điểm, nên người chơi không bị gán
                   vào địa điểm nào - liệt kê nơi giải diễn ra thay vì nói
                   "giải chưa gán địa điểm", vốn sai và gây hoang mang. */
                <>
                  {view.venues.map((v) => (
                    <div key={v.id} style={{ marginBottom: 6 }}>
                      <a
                        className="infolink"
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                          `${v.name} ${v.addr}`
                        )}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {v.name}
                      </a>
                      <div className="small">{v.addr}</div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="v">Giải chưa gán địa điểm</div>
              )}
        </div>
      </div>
    </div>
        {coGiaiThuong ? null : khoiGiaiThuong}
        <div className="jhead">Thể lệ thi đấu</div>
        <TabRule />
      </>
    );
  };

  /* Bốn khối dưới đây là HÀM DỰNG JSX, không phải component — và gọi thẳng
     renderX() chứ không viết <X />.
 
     Lý do: định nghĩa một component bên trong component cha thì mỗi lần cha
     render lại sinh ra một KIỂU component mới, React coi là cây khác nên tháo
     ra dựng lại từ đầu. Sheet và modal có animation biadi-up lúc xuất hiện, nên
     cứ mỗi lần bấm +/- ở ô nhập điểm là animation chạy lại — đúng cái nháy mà
     người dùng thấy.
 
     Đo bằng React 18 thật: kiểu <X /> lồng trong cha cho 4 lần animationstart
     sau 3 lần bấm; gọi renderX() cho đúng 1 lần, không thêm lần nào. */
  /* Thua một trận ở vòng bảng là mất một mạng; ở nhánh loại trực tiếp là về nhà.
     Nói "mất 1 mạng" trong nhánh vừa sai vừa làm người ta tưởng còn cơ hội. */
  const loserOutcome = (current: SwissCurrentMatch) =>
    current.stage === "playoff" ? "người thua dừng bước" : "người thua mất 1 mạng";

  /* Nhãn giai đoạn cho đầu sheet nhập điểm: cặp nhánh mang roundStep của sơ đồ nên
     in "Vòng {n}" ra "Vòng 2" cho một trận chung kết — đúng số nhưng sai tên. */
  const matchRoundLabel = (current: SwissCurrentMatch) =>
    current.stage === "playoff"
      ? (me?.playoff?.roundName ?? `Vòng nhánh ${current.round}`)
      : `Vòng ${current.round}`;

  /* --------------------------------- Màn hình --------------------------------- */

  const ScreenPre = () => (
    <>
      <Navbar brand={false} />
      <div className="pad">
        {/* Đầu màn DÙNG CHUNG một khối với màn đã đăng ký: cùng banner, cùng cỡ
            tên giải, cùng chỗ đặt thanh tab. Trước đây hai màn dựng đầu trang theo
            hai cách khác nhau, nên đăng ký xong là trang nhảy sang một bố cục khác
            - người dùng đọc ra là "vào nhầm trang" chứ không phải "đã đăng ký".

            Hàng "Swiss · 3 mạng · Chạm 5 · N địa điểm · x/y cơ thủ" bỏ khỏi đây vì
            KHÔNG mất gì: mạng/chạm/số vòng nằm sẵn ở đầu tab Thể lệ, còn số cơ thủ
            nằm ở tab Đã đăng ký. Giữ lại chỉ là nói cùng một chuyện hai lần, ngay
            trên chỗ quý nhất của màn hình. */}
        {banner ? (
          <div className="jbanner">
            <img alt={league.name} src={banner} />
            <span className="fmt chip solid">Swiss</span>
          </div>
        ) : null}
        <h1 className="jname">{league.name}</h1>
        <div className="tabs">
          <button className={tab === "reg" ? "" : "on"} onClick={() => setTab("info")}>
            Thông tin chung
          </button>
          <button className={tab === "reg" ? "on" : ""} onClick={() => setTab("reg")}>
            Đã đăng ký
          </button>
        </div>
        <div className="tabpane">
          {tab === "reg" ? <TabRegistered /> : <TabInfo />}
        </div>
        <div className="actionbar-spacer" />
      </div>
      <div className="actionbar">
        <div className="inner">
          {league.fee ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span className="tiny" style={{ marginLeft: "auto" }}>
                Lệ phí {money(league.fee)} đ
              </span>
            </div>
          ) : null}
          {/* Xem giải không cần đăng nhập, nhưng đăng ký thì có. */}
          {user?.id ? (
            <button
              className="btn btn-primary btn-block"
              disabled={busy || !canRegister}
              onClick={() => setModalOpen(true)}
            >
              {regCta}
            </button>
          ) : (
            <button className="btn btn-primary btn-block" onClick={() => navigate("/auth/login")}>
              Đăng nhập để tham gia
            </button>
          )}
        </div>
      </div>
    </>
  );

  const ScreenJoined = () => {
    const active: Tab = tab === "play" || tab === "rank" ? tab : "info";
    return (
      <>
        <Navbar brand={false} />
        <div className="pad">
          {banner ? (
            <div className="jbanner">
              <img alt={league.name} src={banner} />
              <span className="fmt chip solid">Swiss</span>
            </div>
          ) : null}
          <h1 className="jname">{league.name}</h1>
          <div className="tabs">
            <button className={active === "info" ? "on" : ""} onClick={() => setTab("info")}>
              Thông tin chung
            </button>
            <button className={active === "play" ? "on" : ""} onClick={() => setTab("play")}>
              Thi đấu
            </button>
            <button className={active === "rank" ? "on" : ""} onClick={() => setTab("rank")}>
              BXH
            </button>
          </div>
          <div className="tabpane">
            {active === "info" && <TabInfo />}
            {active === "play" && <TabPlay />}
            {active === "rank" && <TabRank />}
          </div>
        </div>
      </>
    );
  };

  const renderResultSheet = () => {
    const current = me.currentMatch;
    if (!current || current.isBye) {
      return null;
    }
    /* Chạm của TRẬN, không phải chạm của giải. Vòng loại trực tiếp đặt được chạm
       riêng, nên lấy league.race thì ô nhập bị chặn ở chạm vòng bảng: trận chung
       kết chạm 9 mà bấm mãi không quá 5, và tỉ số 5-0 gửi lên bị backend từ chối
       vì nó xét theo match.race. */
    const race = current.race || league.race;
    const valid = sheetScore.me === race || sheetScore.opp === race;
    const step = (who: "me" | "opp", delta: number) =>
      setSheetScore((score) => ({
        ...score,
        [who]: Math.max(0, Math.min(race, score[who] + delta)),
      }));

    return (
      <div className="sheetwrap">
        <div className="sheetbd" onClick={() => setSheetOpen(false)} />
        <div className="sheet">
          <div className="grab" />
          <div className="sc">
            <div className="sttl">Nhập kết quả</div>
            <div className="small">
              {matchRoundLabel(current)}
              {current.table ? ` • Bàn ${current.table}` : ""}
              {current.venueName ? ` • ${current.venueName}` : ""} • Chạm {race}
            </div>
            <div className="scoreinput">
              <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                <div className="ava me">{initials(user?.name || "")}</div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Bạn</div>
              </div>
              <div className="stepper">
                <button onClick={() => step("me", -1)}>–</button>
                <span className="sv">{sheetScore.me}</span>
                <button onClick={() => step("me", 1)}>+</button>
              </div>
            </div>
            <div className="scoreinput">
              <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                <Avatar src={current.opponent?.avatar} name={current.opponent?.name || ""} />
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {current.opponent?.name}
                </div>
              </div>
              <div className="stepper">
                <button onClick={() => step("opp", -1)}>–</button>
                <span className="sv">{sheetScore.opp}</span>
                <button onClick={() => step("opp", 1)}>+</button>
              </div>
            </div>
            <div className="tiny" style={{ marginTop: 8 }}>
              {valid ? (
                <>
                  Người thắng:{" "}
                  <b style={{ color: "var(--red)" }}>
                    {sheetScore.me > sheetScore.opp ? "Bạn" : current.opponent?.name}
                  </b>{" "}
                  • {loserOutcome(current)}.
                </>
              ) : (
                <span style={{ color: "var(--st-wait)" }}>Một bên phải đạt {race} ván để kết thúc trận.</span>
              )}
            </div>
            <div className="callout" style={{ marginTop: 14 }}>
              <div className="tiny" style={{ color: "#F0BDB4" }}>
                Kết quả chỉ được tính sau khi đối thủ xác nhận. Sau khi cả hai xác nhận, trận được tính vào rating
                Biadi và không sửa được.
              </div>
            </div>
            <button
              className="btn btn-primary btn-block"
              style={{ marginTop: 14 }}
              disabled={!valid || busy}
              onClick={handleSubmitResult}
            >
              Gửi kết quả
            </button>
            <button className="btn btn-quiet btn-block" style={{ marginTop: 8 }} onClick={() => setSheetOpen(false)}>
              Huỷ
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* Xác nhận là bước không quay lại được (trận vào rating, không sửa nữa), nên
     phải hỏi lại một nhịp và cho người ta nhìn rõ con số mình sắp đồng ý. */
  const renderConfirmResultSheet = () => {
    const current = me.currentMatch;
    const score = pendingScore(current);
    if (!current || !score) {
      return null;
    }
    const iWin = score.me > score.opp;
    return (
      <div className="sheetwrap">
        <div className="sheetbd" onClick={() => setConfirmSheetOpen(false)} />
        <div className="sheet">
          <div className="grab" />
          <div className="sc">
            <div className="sttl">Xác nhận kết quả</div>
            <div className="small">
              {matchRoundLabel(current)}
              {current.table ? ` • Bàn ${current.table}` : ""}
              {current.venueName ? ` • ${current.venueName}` : ""} • Chạm {current.race || league.race}
            </div>
            <div className="pendingscore" style={{ marginTop: 12 }}>
              <span className="ps-side">Bạn</span>
              <span className="ps-val">
                {score.me}<span className="ps-sep">–</span>{score.opp}
              </span>
              <span className="ps-side ps-right">{current.opponent?.name || "Đối thủ"}</span>
            </div>
            <div className="tiny" style={{ marginTop: 8 }}>
              Người thắng: <b style={{ color: "var(--red)" }}>{iWin ? "Bạn" : current.opponent?.name}</b> •{" "}
              {loserOutcome(current)}.
            </div>
            <div className="callout" style={{ marginTop: 14 }}>
              <div className="tiny" style={{ color: "#F0BDB4" }}>
                Sau khi xác nhận, trận được tính vào rating Biadi và không sửa được. Nếu tỉ số chưa đúng, hãy đóng lại
                và chọn "Nhập tỉ số khác".
              </div>
            </div>
            <button
              className="btn btn-primary btn-block"
              style={{ marginTop: 14 }}
              disabled={busy}
              onClick={handleConfirmResult}
            >
              Xác nhận
            </button>
            <button
              className="btn btn-quiet btn-block"
              style={{ marginTop: 8 }}
              onClick={() => setConfirmSheetOpen(false)}
            >
              Đóng
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderConfirmJoinModal = () => (
    <div className="modalwrap">
      <div className="modalbd" onClick={() => setModalOpen(false)} />
      <div className="modal">
        <div className="label" style={{ marginBottom: 10 }}>
          Xác nhận tham gia
        </div>
        <div className="callout">
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{league.name}</div>
          <div className="small" style={{ color: "#E9CFC9" }}>
            Bạn đăng ký tham gia giải này
            {league.fee ? (
              league.feeQrImage || league.feeTransferNote ? (
                <>. Lệ phí {money(league.fee)} đ — đăng ký xong bấm Đóng phí để lấy mã QR</>
              ) : (
                <>. Lệ phí {money(league.fee)} đ đóng tại chỗ khi check-in</>
              )
            ) : null}.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={busy} onClick={handleJoin}>
            Xác nhận
          </button>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setModalOpen(false)}>
            Để sau
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="biadi">
      <div className="shell">{joined ? <ScreenJoined /> : <ScreenPre />}</div>
      {sheetOpen && renderResultSheet()}
      {confirmSheetOpen && renderConfirmResultSheet()}
      {modalOpen && renderConfirmJoinModal()}
      {feeModalOpen && (
        <FeePaymentModal
          league={league}
          feeDeadline={me.feeDeadline ?? league.feeDeadlineDate}
          phone={user?.phone}
          billUploaded={me.billUploaded}
          billImage={me.billImage}
          /* Nạp lại khi đóng, cùng lý do như trang Đấu kép: nộp xong mà không nạp
             thì lần mở sau ô này lại trông như chưa nộp gì. */
          onClose={() => {
            setFeeModalOpen(false);
            load();
          }}
        />
      )}
      <AvatarZoom anh={anhPhongTo} onClose={() => setAnhPhongTo(null)} />
    </div>
  );
}
